/**
 * @deprecated Pre-beta fallback — maps legacy display labels to structured competition.
 * Remove after all Event docs carry `competition` and preset-only inference is unused.
 */

const AGE_OPEN = "open";
const AGE_U15 = "u15";
const AGE_U17 = "u17";
const AGE_U19 = "u19";
const AGE_35 = "35+";
const AGE_40 = "40+";
const AGE_45 = "45+";
const AGE_50 = "50+";

const OPEN_PARTICIPATION = Object.freeze({
  gender: "open",
  age: AGE_OPEN,
});

/** @type {Record<string, { format: string, participation: { gender: string, age: string } }>} */
const EXACT_LABEL_MAP = Object.freeze({
  "Men's Singles": { format: "singles", participation: { gender: "men", age: AGE_OPEN } },
  "Women's Singles": { format: "singles", participation: { gender: "women", age: AGE_OPEN } },
  "Men's Doubles": { format: "doubles", participation: { gender: "men", age: AGE_OPEN } },
  "Women's Doubles": { format: "doubles", participation: { gender: "women", age: AGE_OPEN } },
  "Mixed Doubles": { format: "doubles", participation: { gender: "mixed", age: AGE_OPEN } },
  "Men's Singles Open": { format: "singles", participation: { gender: "men", age: AGE_OPEN } },
  "Women's Singles Open": { format: "singles", participation: { gender: "women", age: AGE_OPEN } },
  "Men's Doubles Open": { format: "doubles", participation: { gender: "men", age: AGE_OPEN } },
  "Women's Doubles Open": { format: "doubles", participation: { gender: "women", age: AGE_OPEN } },
  "Mixed Doubles Open": { format: "doubles", participation: { gender: "mixed", age: AGE_OPEN } },
});

/**
 * @param {string} label
 * @returns {string}
 */
function inferAgeFromLabel(label) {
  const lower = String(label || "").toLowerCase();
  if (/\bu15\b/.test(lower)) return AGE_U15;
  if (/\bu17\b/.test(lower)) return AGE_U17;
  if (/\bu19\b/.test(lower)) return AGE_U19;
  if (/\b50\+|\b50\b/.test(lower)) return AGE_50;
  if (/\b45\+|\b45\b/.test(lower)) return AGE_45;
  if (/\b40\+|\b40\b/.test(lower)) return AGE_40;
  if (/\b35\+|\b35\b/.test(lower)) return AGE_35;
  return AGE_OPEN;
}

/**
 * Heuristic for age-band preset labels (Boys'/Girls'/Mixed … U19, 35+, etc.).
 * @param {string} label
 */
function inferFromLabelHeuristic(label) {
  const t = String(label || "").trim();
  if (!t) return null;
  const lower = t.toLowerCase();
  const isMixed = lower.includes("mixed");
  const isDoubles = lower.includes("doubles");
  const isSingles = lower.includes("singles");
  const format = isDoubles ? "doubles" : isSingles ? "singles" : null;
  if (!format) return null;

  let gender = "open";
  if (isMixed) gender = "mixed";
  else if (lower.includes("men's") || lower.includes("boys'")) gender = "men";
  else if (lower.includes("women's") || lower.includes("girls'")) gender = "women";

  return {
    format,
    participation: { gender, age: inferAgeFromLabel(t) },
  };
}

/**
 * @param {string} displayName
 * @returns {{ format: string, participation: { gender: string, age: string } } | null}
 */
export function resolveLegacyEventCompetition(displayName) {
  const exact = EXACT_LABEL_MAP[String(displayName || "").trim()];
  if (exact) return { ...exact, participation: { ...exact.participation } };
  return inferFromLabelHeuristic(displayName);
}

/**
 * Build a full definition from display name only (legacy path).
 * @param {string} displayName
 */
export function legacyEventDefinitionFromDisplayName(displayName) {
  const name = String(displayName || "").trim();
  if (!name) return null;
  const competition = resolveLegacyEventCompetition(name);
  if (!competition) {
    return {
      display: { name },
      competition: {
        format: "singles",
        participation: { ...OPEN_PARTICIPATION },
      },
    };
  }
  return {
    display: { name },
    competition,
  };
}

export { EXACT_LABEL_MAP as LEGACY_EVENT_LABEL_MAP };
